import React, { useState } from 'react';
import {
  Search, Filter, ChevronDown, Eye, Edit, Send, Download, MoreVertical,
  CheckCircle, Clock, AlertTriangle, DollarSign, FileText, Plus, X
} from 'lucide-react';

const invoicesData = [
  {
    id: 'INV-2026-4521',
    courier: 'FastShip Jamaica',
    prefix: 'FS',
    manifest: 'MAN-2026-02-28-001',
    bags: 4,
    singles: 12,
    amount: 85420,
    status: 'sent',
    dueDate: '2026-03-05',
    createdAt: '2026-02-28'
  },
  {
    id: 'INV-2026-4520',
    courier: 'SpeedyCourier',
    prefix: 'SC',
    manifest: 'MAN-2026-02-28-001',
    bags: 2,
    singles: 8,
    amount: 42150,
    status: 'paid',
    dueDate: '2026-03-04',
    createdAt: '2026-02-27'
  },
  {
    id: 'INV-2026-4519',
    courier: 'IslandExpress',
    prefix: 'IE',
    manifest: 'MAN-2026-02-27-003',
    bags: 3,
    singles: 5,
    amount: 38029,
    status: 'draft',
    dueDate: '2026-03-06',
    createdAt: '2026-02-27'
  },
  {
    id: 'INV-2026-4518',
    courier: 'FastShip Jamaica',
    prefix: 'FS',
    manifest: 'MAN-2026-02-27-002',
    bags: 5,
    singles: 15,
    amount: 72340,
    status: 'overdue',
    dueDate: '2026-02-25',
    createdAt: '2026-02-20'
  },
  {
    id: 'INV-2026-4517',
    courier: 'KP Direct',
    prefix: 'KP',
    manifest: 'MAN-2026-02-26-001',
    bags: 0,
    singles: 25,
    amount: 28800,
    status: 'paid',
    dueDate: '2026-03-02',
    createdAt: '2026-02-26'
  },
];

const statusColors = {
  draft: 'gray',
  sent: 'blue',
  paid: 'lime',
  overdue: 'rose'
};

export default function Invoices() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const filteredInvoices = invoicesData.filter(inv => {
    const matchesSearch = inv.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         inv.courier.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || inv.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const totalPending = invoicesData.filter(i => i.status === 'sent').reduce((sum, i) => sum + i.amount, 0);
  const totalPaid = invoicesData.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.amount, 0);
  const totalOverdue = invoicesData.filter(i => i.status === 'overdue').reduce((sum, i) => sum + i.amount, 0);

  return (
    <div className="invoices-page" data-testid="invoices-page">
      {/* Summary Cards */}
      <div className="invoice-summary-grid">
        <div className="summary-card pending">
          <div className="summary-icon">
            <Clock size={24} />
          </div>
          <div className="summary-content">
            <span className="summary-label">Pending</span>
            <span className="summary-value">${totalPending.toLocaleString()}</span>
            <span className="summary-count">{invoicesData.filter(i => i.status === 'sent').length} invoices</span>
          </div>
        </div>
        <div className="summary-card paid">
          <div className="summary-icon">
            <CheckCircle size={24} />
          </div>
          <div className="summary-content">
            <span className="summary-label">Paid (MTD)</span>
            <span className="summary-value">${totalPaid.toLocaleString()}</span>
            <span className="summary-count">{invoicesData.filter(i => i.status === 'paid').length} invoices</span>
          </div>
        </div>
        <div className="summary-card overdue">
          <div className="summary-icon">
            <AlertTriangle size={24} />
          </div>
          <div className="summary-content">
            <span className="summary-label">Overdue</span>
            <span className="summary-value">${totalOverdue.toLocaleString()}</span>
            <span className="summary-count">{invoicesData.filter(i => i.status === 'overdue').length} invoices</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="page-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="invoice-search"
          />
        </div>
        <div className="toolbar-right">
          <select 
            className="status-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="invoice-status-filter"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </select>
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
            data-testid="create-invoice-btn"
          >
            <Plus size={16} />
            Create Invoice
          </button>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="data-table-card">
        <table className="data-table" data-testid="invoices-table">
          <thead>
            <tr>
              <th>Invoice ID</th>
              <th>Courier</th>
              <th>Manifest</th>
              <th>Items</th>
              <th>Amount (JMD)</th>
              <th>Status</th>
              <th>Due Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map((invoice) => (
              <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                <td>
                  <span className="invoice-id">{invoice.id}</span>
                </td>
                <td>
                  <div className="courier-cell">
                    <span className={`courier-prefix ${invoice.prefix.toLowerCase()}`}>{invoice.prefix}</span>
                    <span className="courier-name">{invoice.courier}</span>
                  </div>
                </td>
                <td>
                  <span className="manifest-link">{invoice.manifest}</span>
                </td>
                <td>
                  <div className="items-cell">
                    <span className="item-count bags">{invoice.bags} bags</span>
                    <span className="item-count singles">{invoice.singles} singles</span>
                  </div>
                </td>
                <td>
                  <span className="amount">${invoice.amount.toLocaleString()}</span>
                </td>
                <td>
                  <span className={`status-badge ${invoice.status}`}>
                    {invoice.status === 'paid' && <CheckCircle size={12} />}
                    {invoice.status === 'sent' && <Clock size={12} />}
                    {invoice.status === 'draft' && <FileText size={12} />}
                    {invoice.status === 'overdue' && <AlertTriangle size={12} />}
                    {invoice.status}
                  </span>
                </td>
                <td>
                  <span className={invoice.status === 'overdue' ? 'overdue-date' : ''}>
                    {invoice.dueDate}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="action-btn" 
                      title="View/Edit"
                      onClick={() => setSelectedInvoice(invoice)}
                      data-testid={`view-${invoice.id}`}
                    >
                      <Eye size={16} />
                    </button>
                    {invoice.status === 'draft' && (
                      <button className="action-btn" title="Send" data-testid={`send-${invoice.id}`}>
                        <Send size={16} />
                      </button>
                    )}
                    <button className="action-btn" title="Download PDF" data-testid={`download-${invoice.id}`}>
                      <Download size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="modal-overlay" onClick={() => setSelectedInvoice(null)}>
          <div className="modal-content invoice-detail-modal" onClick={e => e.stopPropagation()} data-testid="invoice-detail-modal">
            <div className="modal-header">
              <div>
                <h3>Invoice {selectedInvoice.id}</h3>
                <span className={`status-badge ${selectedInvoice.status}`}>{selectedInvoice.status}</span>
              </div>
              <button className="close-btn" onClick={() => setSelectedInvoice(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="invoice-detail-grid">
                <div className="detail-section">
                  <h4>Bill To</h4>
                  <p className="courier-name-large">{selectedInvoice.courier}</p>
                  <p className="courier-detail">Prefix: {selectedInvoice.prefix}</p>
                </div>
                <div className="detail-section">
                  <h4>Invoice Details</h4>
                  <p>Manifest: {selectedInvoice.manifest}</p>
                  <p>Created: {selectedInvoice.createdAt}</p>
                  <p>Due Date: {selectedInvoice.dueDate}</p>
                </div>
              </div>

              <div className="invoice-items-section">
                <h4>Line Items</h4>
                <table className="invoice-items-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Service Contribution (Bags)</td>
                      <td>{selectedInvoice.bags}</td>
                      <td>$1,000</td>
                      <td>${(selectedInvoice.bags * 1000).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td>Service Contribution (Singles)</td>
                      <td>{selectedInvoice.singles}</td>
                      <td>$1,500</td>
                      <td>${(selectedInvoice.singles * 1500).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td>Pallet Fee (Bags)</td>
                      <td>{selectedInvoice.bags}</td>
                      <td>$1,296</td>
                      <td>${(selectedInvoice.bags * 1296).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td>Pallet Fee (Singles)</td>
                      <td>{selectedInvoice.singles}</td>
                      <td>$648</td>
                      <td>${(selectedInvoice.singles * 648).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td>Delivery Fee</td>
                      <td>1</td>
                      <td>$1,000</td>
                      <td>$1,000</td>
                    </tr>
                    <tr>
                      <td>Freight Charge</td>
                      <td>-</td>
                      <td>-</td>
                      <td>${(selectedInvoice.amount - (selectedInvoice.bags * 2296) - (selectedInvoice.singles * 2148) - 1000).toLocaleString()}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3"><strong>Total</strong></td>
                      <td><strong>${selectedInvoice.amount.toLocaleString()}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {selectedInvoice.status === 'draft' && (
                <div className="adjustment-section">
                  <h4>Add Adjustment</h4>
                  <div className="adjustment-form">
                    <input type="text" placeholder="Description (e.g., Storage Fee)" />
                    <input type="number" placeholder="Amount (JMD)" />
                    <button className="btn btn-secondary">
                      <Plus size={14} /> Add
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelectedInvoice(null)}>Close</button>
              {selectedInvoice.status === 'draft' && (
                <>
                  <button className="btn btn-secondary">
                    <Edit size={14} /> Edit
                  </button>
                  <button className="btn btn-primary">
                    <Send size={14} /> Send Invoice
                  </button>
                </>
              )}
              <button className="btn btn-primary">
                <Download size={14} /> Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} data-testid="create-invoice-modal">
            <div className="modal-header">
              <h3>Create New Invoice</h3>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Select Manifest</label>
                <select data-testid="manifest-select">
                  <option value="">Choose a manifest...</option>
                  <option value="MAN-2026-02-27-002">MAN-2026-02-27-002 (Pending)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Select Courier</label>
                <select data-testid="courier-select">
                  <option value="">Choose a courier...</option>
                  <option value="FS">FastShip Jamaica</option>
                  <option value="SC">SpeedyCourier</option>
                  <option value="IE">IslandExpress</option>
                  <option value="KP">KP Direct</option>
                </select>
              </div>
              <p className="form-hint">
                Invoice will be auto-calculated based on manifest items and fee schedule.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="btn btn-primary" data-testid="generate-invoice-btn">
                Generate Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
